import { MouseEvent as ReactMouseEvent, useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { Map } from '@/components/Map';
import { FilterPriceRegionModal } from '@/components/Modals/FilterPriceRegionModal';
import PriceComparativeChartModal, {
  IPriceComparativeChartModalProps,
} from '@/components/Modals/PriceComparativeChartModal';
import { Pagination } from '@/components/Pagination';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { PageLayout } from '@/layouts/PageLayout';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { IPagination } from '@/types';
import { ILocation } from '@/types/api/locations';
import { IPricingComparative } from '@/types/api/pricingsComparativeRegion';
import { formatMicroValueWithoutSymbol } from '@/utils';
import { paramsSerializer } from '@/utils';
import { handleErrorAsync } from '@/utils/handlers';
import { removeFalsyValues } from '@/utils/objectUtils';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalShippingOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface ChartProps {
  initialItem: IPriceComparativeChartModalProps['initialItem'];
  initialBrand: IPriceComparativeChartModalProps['initialBrand'];
}

interface IRegionPriceProps {
  locationsProps: ILocation[];
  comparativeItemsProps: IPricingComparative[];
  countComparativeItemsProps: number;
}

interface ILocationList {
  id: number;
  name: string;
  code: string;
}

const StyledTableCell = styled(TableCell)(() => ({
  margin: '12px 0px',
  [`&.${tableCellClasses.head}`]: {
    padding: '12px 0px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 0,
  },
  '&:first-of-type': {
    paddingLeft: '8px',
  },
  '&:not(:first-of-type)': {
    textAlign: 'center',
  },
  border: 0,
  '& p': {
    padding: '0 4px',
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)<{ valueIndex: number }>(
  ({ valueIndex }) => ({
    backgroundColor: valueIndex % 2 === 0 ? '#F7F7F7' : '#FFFFFF',

    '&:last-child td, &:last-child th': {
      border: 0,
    },
  })
);

const PAGE_SIZE = 20;

const RegionPrice = ({
  locationsProps,
  comparativeItemsProps,
  countComparativeItemsProps,
}: IRegionPriceProps) => {
  const stateLocations = locationsProps
    .filter(location => location.type === 'state')
    .map(stateLocation => ({
      id: stateLocation.id,
      name: stateLocation.state.name,
      code: stateLocation.state.code,
    }));

  const countryLocations = locationsProps
    .filter(location => location.type === 'country')
    .map(countryLocation => ({
      id: countryLocation.id,
      name: countryLocation.country.name,
      code: countryLocation.country.code,
    }));

  const { setIsLoading } = useLoading();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const router = useRouter();
  const [count, setCount] = useState(countComparativeItemsProps);
  const [comparativeItems, setComparativeItems] = useState(
    comparativeItemsProps
  );

  const [location, setLocation] = useState('Brasil');
  const [selectLocation, setSelectLocation] = useState({
    name: '',
    code: '',
  });
  const [isOpenRegionPriceModal, setIsOpenRegionPriceModal] = useState(false);
  const [clickedIndex, setClickedIndex] = useState<null | number>(null);
  const [pagination, setPagination] = useState<IPagination>({
    currentPage: Number(router.query.page) || 1,
    totalPages: Math.ceil(count / PAGE_SIZE),
  });

  const [openComparativeChart, setOpenComparativeChart] = useState(false);
  const [comparativeChartProps, setComparativeChartProps] = useState(
    {} as ChartProps
  );

  const [allowedLocations, setAllowedLocations] = useState<ILocationList[]>(
    stateLocations.concat(countryLocations).reverse()
  );

  const prevLocations = stateLocations.concat(countryLocations).reverse();

  const [searchValue, setSearchValue] = useState('');

  const changeQueryParams = (
    queryValue: string | undefined,
    queryName: 'item' | 'localidade'
  ) => {
    const newQuery = {
      ...router.query,
      [queryName]: queryValue,
      page: queryName === 'localidade' ? 1 : pagination.currentPage,
    };

    return router.push(
      { pathname: router.pathname, query: newQuery },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const handleScrollToTable = () => {
    const element = document.getElementById('tableContainer');
    if (element) {
      window.scrollBy({
        top: element.getBoundingClientRect().top - 80,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const getItemComparativeWithFilterParams = async (
    filterParams: Partial<ObjectLiteral>,
    id: number | undefined
  ) => {
    const params = removeFalsyValues(filterParams);
    const response = await api.get<{
      results: IPricingComparative[];
      count: number;
    }>(`/api/items/comparative/?shipping_location=${id}`, {
      params: {
        ...params,
        page_size: PAGE_SIZE,
      },
      paramsSerializer,
    });
    saveQueryParamsInUrl(params);
    return response;
  };

  function saveQueryParamsInUrl(params: {}) {
    router.push(
      {
        pathname: '/preco-regiao',
        query: params,
      },
      undefined,
      { shallow: true }
    );

    return;
  }

  const handleChangePage = async (page: number) => {
    const allowedLocation = allowedLocations.find(
      allowedLocation => allowedLocation.name === location
    );
    const { data } = await getItemComparativeWithFilterParams(
      {
        ...router.query,
        page,
      },
      allowedLocation?.id
    );
    setPagination({
      currentPage: page,
      totalPages: Math.ceil(data.count / PAGE_SIZE),
    });
    setComparativeItems(data.results);
    setCount(data.count);
  };

  const handleOpenModal = (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    values: ChartProps
  ) => {
    const itemName = values.initialItem?.name.replace(/\s+/g, '-');

    e.stopPropagation();
    setOpenComparativeChart(true);
    setComparativeChartProps(values);

    changeQueryParams(itemName, 'item');
  };

  const handleSelectLocation = (location: string) => {
    const isItemNameSelected = allowedLocations.find(itemLocationList => {
      return itemLocationList.name === location;
    });
    return isItemNameSelected?.name;
  };

  const handleSelectLocationChange = async (
    location: string,
    code?: string
  ) => {
    const allowedLocation = allowedLocations.find(
      allowedLocation => allowedLocation.name === location
    );

    const [comparativeItemsResponse, error] = await handleErrorAsync(
      async () => {
        setIsLoading(true);
        const {
          data: { results, count },
        } = await getItemComparativeWithFilterParams(
          {
            ...router.query,
            page: 1,
          },
          allowedLocation?.id
        );

        return { results, count };
      }
    ).finally(() => {
      setIsLoading(false);
    });

    if (error) {
      Toast('error', error.message);
      return;
    }

    changeQueryParams(code, 'localidade');

    setPagination({
      currentPage: 1,
      totalPages: Math.ceil(comparativeItemsResponse.count / PAGE_SIZE),
    });

    setComparativeItems(comparativeItemsResponse.results);
    setCount(comparativeItemsResponse.count);
    setSelectLocation({ ...selectLocation, name: location });
    setLocation(location);

    handleScrollToTable();
  };

  useEffect(() => {
    setSelectLocation({ ...selectLocation, name: location });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenRegionPriceModal]);

  const chartInitialLocation = allowedLocations.find(
    allowedLocation => allowedLocation.name === location
  ) as ILocationList;

  return (
    <PageLayout title="Preços por região" removeIconBack>
      <MainContainer component="main">
        <Card sx={{ px: 2, py: 3, display: 'flex', flexDirection: 'column' }}>
          <Button
            onClick={() => setIsOpenRegionPriceModal(!isOpenRegionPriceModal)}
            sx={{ alignSelf: smUp ? 'end' : 'center', mb: 2 }}
          >
            Selecione uma localidade
          </Button>
          <Map
            onChange={(location, code) =>
              handleSelectLocationChange(location, code)
            }
            selectLocation={handleSelectLocation(location)}
            width={smUp ? 600 : 400}
            height={smUp ? 550 : 350}
          />
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            changePage={handleChangePage}
            count={count}
          >
            <TableContainer
              sx={theme => ({
                mt: 4,
                mb: 3,
                [theme.breakpoints.up('md')]: {
                  border: '1px solid rgba(0, 0, 0, 0.20)',
                  borderRadius: '12px',
                  p: 2,
                },
              })}
              id="tableContainer"
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
                  {location}&nbsp;
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }}
                >
                  (Últimos 30 dias)
                </Typography>
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Item</StyledTableCell>
                    <StyledTableCell align="right">Mínimo (R$)</StyledTableCell>
                    <StyledTableCell align="right">Médio (R$)</StyledTableCell>
                    {smUp && (
                      <StyledTableCell align="right">
                        Máximo (R$)
                      </StyledTableCell>
                    )}
                    <StyledTableCell align="right">
                      {smUp ? 'Fornecedores' : <LocalShippingOutlined />}
                    </StyledTableCell>
                    {smUp && (
                      <StyledTableCell sx={{ width: '90px' }} align="right">
                        Histórico
                      </StyledTableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparativeItems.map((comparative, index) => (
                    <>
                      {comparative.brands.length > 0 ? (
                        <>
                          <StyledTableRow
                            key={comparative.id}
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              setClickedIndex(
                                comparative.id === clickedIndex
                                  ? null
                                  : comparative.id
                              )
                            }
                            valueIndex={index}
                          >
                            <StyledTableCell
                              component="th"
                              scope="row"
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              {comparative.id === clickedIndex ? (
                                <KeyboardArrowUp
                                  fontSize="small"
                                  sx={{ color: 'text.secondary', mr: 1 }}
                                />
                              ) : (
                                <KeyboardArrowDown
                                  fontSize="small"
                                  sx={{ color: 'text.secondary', mr: 1 }}
                                />
                              )}
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {comparative.name}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {comparative.log_minimum_price_micros
                                  ? formatMicroValueWithoutSymbol(
                                      comparative.log_minimum_price_micros
                                    )
                                  : '-'}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {comparative.log_average_price_micros
                                  ? formatMicroValueWithoutSymbol(
                                      comparative.log_average_price_micros
                                    )
                                  : '-'}
                              </Typography>
                            </StyledTableCell>
                            {smUp && (
                              <StyledTableCell align="right">
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                >
                                  {comparative.log_maximum_price_micros
                                    ? formatMicroValueWithoutSymbol(
                                        comparative.log_maximum_price_micros
                                      )
                                    : '-'}
                                </Typography>
                              </StyledTableCell>
                            )}
                            <StyledTableCell align="right">
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {comparative.log_suppliers_count}
                              </Typography>
                            </StyledTableCell>
                            {smUp && (
                              <StyledTableCell align="right">
                                <Button
                                  sx={{
                                    textTransform: 'none',
                                  }}
                                  onClick={e =>
                                    handleOpenModal(e, {
                                      initialItem: comparative,
                                      initialBrand: null,
                                    })
                                  }
                                >
                                  Ver gráfico
                                </Button>
                              </StyledTableCell>
                            )}
                          </StyledTableRow>

                          {comparative.brands.map(brand => (
                            <StyledTableRow key={brand.id} valueIndex={index}>
                              <StyledTableCell component="th" scope="row">
                                <Collapse in={comparative.id === clickedIndex}>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ marginLeft: '46px', marginY: '12px' }}
                                  >
                                    {brand.name}
                                  </Typography>
                                </Collapse>
                              </StyledTableCell>
                              <StyledTableCell align="center">
                                <Collapse in={comparative.id === clickedIndex}>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    {brand.log_minimum_price_micros
                                      ? formatMicroValueWithoutSymbol(
                                          brand.log_minimum_price_micros
                                        )
                                      : '-'}
                                  </Typography>
                                </Collapse>
                              </StyledTableCell>
                              <StyledTableCell align="right">
                                <Collapse in={comparative.id === clickedIndex}>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    {brand.log_average_price_micros
                                      ? formatMicroValueWithoutSymbol(
                                          brand.log_average_price_micros
                                        )
                                      : '-'}
                                  </Typography>
                                </Collapse>
                              </StyledTableCell>
                              {smUp && (
                                <StyledTableCell align="right">
                                  <Collapse
                                    in={comparative.id === clickedIndex}
                                  >
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {brand.log_maximum_price_micros
                                        ? formatMicroValueWithoutSymbol(
                                            brand.log_maximum_price_micros
                                          )
                                        : '-'}
                                    </Typography>
                                  </Collapse>
                                </StyledTableCell>
                              )}
                              <StyledTableCell align="right">
                                <Collapse in={comparative.id === clickedIndex}>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                  >
                                    {brand.log_suppliers_count}
                                  </Typography>
                                </Collapse>
                              </StyledTableCell>
                              {smUp && (
                                <StyledTableCell align="right">
                                  <Collapse
                                    in={comparative.id === clickedIndex}
                                  >
                                    <Button
                                      sx={{ textTransform: 'none' }}
                                      onClick={e =>
                                        handleOpenModal(e, {
                                          initialItem: comparative,
                                          initialBrand: brand,
                                        })
                                      }
                                    >
                                      Ver gráfico
                                    </Button>
                                  </Collapse>
                                </StyledTableCell>
                              )}
                            </StyledTableRow>
                          ))}
                        </>
                      ) : (
                        <StyledTableRow
                          key={comparative.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                          valueIndex={index}
                        >
                          <StyledTableCell component="th" scope="row">
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ marginY: '12px' }}
                            >
                              {comparative.name}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Typography variant="body1" color="text.secondary">
                              {comparative.log_minimum_price_micros
                                ? formatMicroValueWithoutSymbol(
                                    comparative.log_minimum_price_micros
                                  )
                                : '-'}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography variant="body1" color="text.secondary">
                              {comparative.log_average_price_micros
                                ? formatMicroValueWithoutSymbol(
                                    comparative.log_average_price_micros
                                  )
                                : '-'}
                            </Typography>
                          </StyledTableCell>
                          {smUp && (
                            <StyledTableCell align="right">
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                {comparative.log_maximum_price_micros
                                  ? formatMicroValueWithoutSymbol(
                                      comparative.log_maximum_price_micros
                                    )
                                  : '-'}
                              </Typography>
                            </StyledTableCell>
                          )}
                          <StyledTableCell align="right">
                            <Typography variant="body1" color="text.secondary">
                              {comparative.log_suppliers_count}
                            </Typography>
                          </StyledTableCell>
                          {smUp && (
                            <StyledTableCell align="right">
                              <Button
                                sx={{ textTransform: 'none' }}
                                onClick={e =>
                                  handleOpenModal(e, {
                                    initialItem: comparative,
                                    initialBrand: null,
                                  })
                                }
                              >
                                Ver gráfico
                              </Button>
                            </StyledTableCell>
                          )}
                        </StyledTableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Pagination>
          {!smUp && (
            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={e =>
                handleOpenModal(e, {
                  initialItem: comparativeItems[0],
                  initialBrand: null,
                })
              }
            >
              visualizar histórico de preços
            </Button>
          )}
        </Card>
      </MainContainer>

      <FilterPriceRegionModal
        title="Localidade"
        inputLabel="Pesquise por localidade"
        open={isOpenRegionPriceModal}
        handleClose={setIsOpenRegionPriceModal}
        selectOption={selectLocation.name}
        setOption={() => {
          handleSelectLocationChange(selectLocation.name, selectLocation.code);
          setLocation;
        }}
        onFilter={value => {
          const lowercaseSearchValue = value.toLowerCase();

          if (lowercaseSearchValue !== searchValue) {
            setSearchValue(lowercaseSearchValue);
            setAllowedLocations(() => {
              const filteredLocations = prevLocations.filter(prevLocation => {
                return prevLocation.name
                  .toLowerCase()
                  .includes(lowercaseSearchValue);
              });

              return filteredLocations.length > 0 ? filteredLocations : [];
            });
          }
        }}
      >
        <>
          {allowedLocations.map(allowedLocation => {
            return (
              <>
                <ListItem key={allowedLocation.id} disablePadding>
                  <ListItemButton
                    onClick={() =>
                      setSelectLocation({
                        name: allowedLocation.name,
                        code: allowedLocation.code,
                      })
                    }
                    dense
                  >
                    <ListItemIcon>
                      <Radio
                        edge="start"
                        checked={selectLocation.name === allowedLocation.name}
                      />
                    </ListItemIcon>
                    <ListItemText primary={allowedLocation.name} />
                  </ListItemButton>
                </ListItem>
                {allowedLocation.id === 1 && <Divider />}
              </>
            );
          })}
        </>
      </FilterPriceRegionModal>

      {openComparativeChart && (
        <PriceComparativeChartModal
          isOpen={openComparativeChart}
          handleClose={() => setOpenComparativeChart(false)}
          onChangeLocation={handleSelectLocationChange}
          items={comparativeItems}
          locations={allowedLocations}
          initialLocation={chartInitialLocation}
          {...comparativeChartProps}
        />
      )}
    </PageLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  try {
    const types = ['country', 'state'];
    const queryString = `types=${types.join('&types=')}`;

    const { page } = context.query;

    const [{ data: locations }, { data: comparativeItems }] = await Promise.all(
      [
        api.get(`/api/locations/?${queryString}`),
        api.get(
          `/api/items/comparative/?shipping_location=1&page=${page || 1}`
        ),
      ]
    );

    return {
      props: {
        locationsProps: locations.results,
        comparativeItemsProps: comparativeItems.results,
        countComparativeItemsProps: comparativeItems.count,
      },
    };
  } catch (error) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
};

export default RegionPrice;
export type { ILocationList };
